import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import classNames from "classnames";
import { useForm } from "react-hook-form";
import OsteopataContext from "../../context/osteopata";
import UserContext from "../../context/user";
import useOsteopathCreateMutation from "../../hooks/mutations/useOsteopathCreateMutation";
import useOsteopathUpdateMutation from "../../hooks/mutations/useOsteopathUpdateMutation";
import useGetOsteopathAvailabilities from "../../hooks/queries/useGetOsteopathAvailabilities";
import useGetOsteopaths from "../../hooks/queries/useGetOsteopaths";
import useGetOsteopathTimeSlots from "../../hooks/queries/useGetOsteopathTimeSlots";
import { getQuestionLabel, parseRequest, QUESTIONS } from "../../utils/osteopata";
import { emailRegex, getSameEmailRegex } from "../../utils/regex";
import Icon from "../icon";
import Question from "../question";
import OsteopataCalendar from "./osteopata-calendar";

const OsteopataSurvey = () => {
  const { userProfile } = useContext(UserContext);
  const { survey, currentRequest, labels, appLang } = useContext(OsteopataContext);

  const [selectedOsteopath, setSelecetedOsteopath] = useState();
  const [selectedDate, setSelectedDate] = useState();
  const selectedDay = useMemo(() => selectedDate?.format("DD-MM-YYYY"), [selectedDate]);
  const [selectedTime, setSelectedTime] = useState();
  const [validationError, setValidationError] = useState();

  const {
    isFetching: isOsteopathsFetching,
    data: osteopaths,
    error: osteopathsError,
  } = useGetOsteopaths(setSelecetedOsteopath);

  const {
    isFetching: isAvailabilitiesFetching,
    error: availabilitiesError,
    data: availabilities,
  } = useGetOsteopathAvailabilities(selectedOsteopath);

  const {
    isFetching: isTimeSlotsFetching,
    error: timeSlotsError,
    data: timeSlots,
    refetch: refetchTimeSlots,
  } = useGetOsteopathTimeSlots(selectedOsteopath, selectedDay, currentRequest);

  const [formData, setFormData] = useState();

  const {
    register,
    handleSubmit,
    control,
    reset,
    watch,
    trigger,
    formState: { errors, touchedFields, dirtyFields },
  } = useForm({
    mode: "onTouched",
    defaultValues: useMemo(() => formData, [formData]),
  });

  const formWatch = watch();
  const initializedFormData = useRef();

  useEffect(() => {
    if (initializedFormData.current) {
      reset(formData);
    }
  }, [reset, formData]);

  useEffect(() => {
    if (!initializedFormData.current && userProfile) {
      initializedFormData.current = true;
      const initializedFields = {
        name: userProfile?.firstName,
        surname: userProfile?.lastName,
      };
      const {
        requestOsteopath,
        requestBooking,
        formData: requestFormData,
      } = parseRequest(currentRequest, userProfile);
      setFormData((current) => ({ ...current, ...initializedFields, ...requestFormData }));
      setSelecetedOsteopath(requestOsteopath?.id);
      setSelectedDate(requestBooking);
      setSelectedTime(true);
    }
  }, [userProfile, setFormData, currentRequest]);

  const handleOsteopathSelection = (e) => {
    const value = e.currentTarget.name;
    setSelecetedOsteopath((current) => (current === Number(value) ? null : Number(value)));
    setSelectedDate();
    setSelectedTime();
  };

  const handleTimeSlotSelection = (e) => {
    const selectedTimeSlot = e?.currentTarget?.id;
    if (selectedTimeSlot) {
      const [h, m] = selectedTimeSlot.split(":");
      setSelectedDate((current) => current.set("hour", h).set("minute", m));
      setSelectedTime(true);
    }
  };

  const { mutateAsync: createRequest, error: creationError } = useOsteopathCreateMutation();

  const { mutateAsync: updateRequest, error: updateError } = useOsteopathUpdateMutation();

  const handleRefreshSlots = () => {
    refetchTimeSlots();
    setSelectedDate((current) => current.set("hour", 0).set("minute", 0));
  };

  const onSubmit = useCallback(
    async (values) => {
      if (!selectedOsteopath || !selectedDate || !selectedTime) {
        setValidationError(true);
      } else {
        if (currentRequest?.editMode) {
          await updateRequest({
            requestId: currentRequest?.id,
            formData: values,
            selectedDate,
            osteopathId: selectedOsteopath,
          });
        } else {
          await createRequest({
            osteopathId: selectedOsteopath,
            formData: values,
            selectedDate,
          });
        }
      }
    },
    [selectedTime, selectedDate, selectedOsteopath, createRequest, updateRequest, currentRequest]
  );

  return (
    <div className="wrapper wrapper--app wrapper-small">
      <div className="app-content__title-wrapper">
        <h2 className="app-content__title">
          {labels?.WELFARE_OSTEOPATA_SURVEY_SECTION_TITLE || "Compila i tuoi dati"}
        </h2>
      </div>
      <form onSubmit={handleSubmit(onSubmit)} className="form osteopata-content__form" noValidate>
        <div className="form__question-wrapper">
          {QUESTIONS?.map((question, i) => (
            <Question
              question={{
                ...question,
                label: getQuestionLabel(question.id, labels),
                answers: question?.answers?.map((ans) => ({
                  ...ans,
                  label: getQuestionLabel(ans.id, labels),
                })),
              }}
              key={question.id}
              register={register}
              error={errors[`${question.id}`]}
              touched={touchedFields[`${question.id}`]}
              isDirty={dirtyFields[`${question.id}`]}
              control={control}
              trigger={trigger}
              readOnlyMode={i < 2}
              labels={labels}
              pattern={
                question.id === "email"
                  ? emailRegex
                  : question.id === "email-repeat"
                  ? getSameEmailRegex(formWatch.email)
                  : null
              }
              customPatternErrorMsg={
                question.id === "email"
                  ? labels?.WELFARE_NOT_VALID_EMAIL || "The inserted email is not valid"
                  : question.id === "email-repeat"
                  ? labels?.WELFARE_NOT_CORRESPONDING_EMAIL ||
                    "The inserted email does not correspond"
                  : null
              }
              value={formWatch[`${question.id}`]}
              attachments={survey?.attachments}
              trim={["email", "email-repeat"].includes(question.id)}
            />
          ))}
        </div>
        <h2 className="app-content__title">
          {labels?.WELFARE_OSTEOPATA_OSTEOPATA_CHOOSE || "Scegli osteopata"}
        </h2>
        {osteopathsError ? (
          <div className="form__error text-center">Error retrieving osteopath availabilities</div>
        ) : isOsteopathsFetching ? (
          <div className="loading-spinner" />
        ) : (
          <>
            {osteopaths?.length > 0 ? (
              <div className="osteopata-content__list">
                {osteopaths?.map((osteopath) => (
                  <button
                    className={classNames(
                      "osteopata-content__card",
                      osteopath.id === selectedOsteopath && "osteopata-content__card--selected"
                    )}
                    key={osteopath.id}
                    name={osteopath.id}
                    type="button"
                    onClick={handleOsteopathSelection}
                    disabled={osteopaths?.length === 1}
                  >
                    <div className="osteopata-content__card-title">{osteopath.name}</div>
                    {osteopath?.price && (
                      <div className="osteopata-content__card-info">
                        <Icon name="money" className="money" />
                        {osteopath.price}
                      </div>
                    )}
                    {osteopath?.workingHours && (
                      <div className="osteopata-content__card-info">
                        <Icon name="clock" />
                        {osteopath.workingHours}
                      </div>
                    )}
                  </button>
                ))}
              </div>
            ) : (
              <div className="form__error text-center">No osteopath found</div>
            )}
            {!!selectedOsteopath && (
              <>
                <div className="osteopata-content__calendar">
                  <h2 className="app-content__title">
                    {labels?.WELFARE_OSTEOPATA_NEXT_DATES || "Prossime date disponibili"}
                  </h2>
                </div>
                {availabilitiesError ? (
                  <div className="form__error text-center">
                    Error retrieving osteopath availabilities
                  </div>
                ) : isAvailabilitiesFetching ? (
                  <div className="loading-spinner mt-s" />
                ) : availabilities?.length === 0 ? (
                  <div className="text-center">
                    {labels?.WELFARE_OSTEOPATA_NO_AVAILABILITIES ||
                      "No availabilities for the selected osteopath"}
                  </div>
                ) : (
                  <OsteopataCalendar
                    availabilities={availabilities}
                    lang={appLang}
                    selectedDate={selectedDate}
                    setSelectedDate={setSelectedDate}
                    setSelectedTime={setSelectedTime}
                  />
                )}
              </>
            )}
            {!!selectedDate && (
              <>
                <div className="osteopata-content__time-slots">
                  <h2 className="app-content__title">
                    {labels?.WELFARE_OSTEOPATA_AVAILABILITIES || "Orari disponibili"}
                  </h2>
                </div>
                {timeSlotsError ? (
                  <div className="form__error text-center">
                    Error retrieving osteopath availabilities
                  </div>
                ) : isTimeSlotsFetching ? (
                  <div className="loading-spinner mt-s" />
                ) : timeSlots?.length === 0 ? (
                  <div className="text-center">
                    {labels?.WELFARE_OSTEOPATA_NO_AVAILABILITIES ||
                      "No availabilities for the selected osteopath"}
                  </div>
                ) : (
                  <div className="pick-up-slots__times">
                    {timeSlots?.map((time, i) => (
                      <button
                        key={i}
                        id={time.format("HH:mm")}
                        className={classNames(
                          "pick-up-slots__time",
                          selectedDate.isSame(time, "minute") && "pick-up-slots__time--selected"
                        )}
                        onClick={handleTimeSlotSelection}
                        type="button"
                      >
                        <span>{time.format("HH:mm")}</span>
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}
          </>
        )}
        <div className="form__buttons-wrapper form__buttons-wrapper--static">
          {validationError && (
            <p className="form__error text-center">
              {labels?.WELFARE_OSTEOPATA_VALIDATION_ERROR || "Some fields are missing"}
            </p>
          )}
          {(creationError || updateError) &&
            ((creationError || updateError)?.status === 403 ? (
              <div className="pick-up-slots__booked-slot">
                <div className="error text-center">
                  {labels?.WELFARE_BOOKED_SLOT_TITLE || "Lo slot che hai richiesto non c'è più"}
                </div>
                <button className="link link--text" onClick={handleRefreshSlots} type="button">
                  {labels?.WELFARE_CART_RELOAD_SLOTS || "Reload available slots"}
                </button>
              </div>
            ) : (
              <p className="form__error text-center">
                {labels?.WELFARE_OSTEOPATA_ERROR_SAVING_REQUEST || "Error saving requests"}
              </p>
            ))}
          <div className="form__buttons form__buttons--center">
            <button className="button primary form__submit">
              {labels?.WELFARE_OSTEOPATA_FORM_CTA || "Prenota visita"}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default OsteopataSurvey;
